/*@jsxRuntime automatic @jsxImportSource react*/
/*<!-- vale off -->*/
/*<!-- vale on -->*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Amazon Web Services", description = "Grant Hightouch access to resources within your AWS account.", lastUpdatedDate = new Date(1731542400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Amazon Web Services",
        description: "Grant Hightouch access to resources within your AWS account.",
        lastUpdatedDate: "Thu Nov 14 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Setup",
          "depth": 0,
          "id": "setup"
        }, {
          "value": "Cross-account role",
          "depth": 1,
          "id": "cross-account-role"
        }, {
          "value": "Access key",
          "depth": 1,
          "id": "access-key"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "CredentialsError: Missing credentials in configuration",
          "depth": 1,
          "id": "credentialserror-missing-credentials-in-configuration"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      ul: "ul",
      li: "li",
      a: "a",
      strong: "strong",
      img: "img",
      h3: "h3",
      ol: "ol",
      code: "code"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch's AWS integration powers several features:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Workspace deployment on AWS (see ", _jsx(_components.a, {
            href: "/security/regions#supported-regions",
            children: "Regions"
          }), ")"]
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/security/storage#self-hosted-storage",
            children: "Self-hosted storage via S3"
          })
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/sources/amazon-athena",
            children: "Athena"
          }), " source"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/sources/s3",
            children: "S3"
          }), " source"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/destinations/s3",
            children: "S3"
          }), " destination"]
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/security/aws/privatelink",
            children: "AWS PrivateLink"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "setup",
        children: "Setup"
      }), "\n", _jsxs(_components.p, {
        children: ["To give Hightouch access to resources on your AWS account,\ngo to the ", _jsx(_components.a, {
          href: "https://app.hightouch.com/settings/cloud-providers?from=docs",
          children: _jsx(_components.strong, {
            children: "Cloud providers"
          })
        }), " tab on the Settings page.\nClick ", _jsx(_components.strong, {
          children: "Add cloud provider"
        }), ", and select ", _jsx(_components.strong, {
          children: "Amazon Web Services"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/cloud-providers/add-new-credential.png",
          alt: "Adding a new cloud provider in Hightouch"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Give your credential a ", _jsx(_components.strong, {
          children: "Display name"
        }), ", then select either ", _jsx(_components.strong, {
          children: "Cross-account role"
        }), " or ", _jsx(_components.strong, {
          children: "Access key"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "cross-account-role",
        children: "Cross-account role"
      }), "\n", _jsx(_components.p, {
        children: "Cross-account roles are a mechanism provided by AWS to allow you to grant secure access to Hightouch without requiring that you hand over sensitive secrets like Secret Access Keys."
      }), "\n", _jsxs(_components.p, {
        children: ["The Hightouch UI displays an ", _jsx(_components.strong, {
          children: "AWS account ID"
        }), " and a randomly generated ", _jsx(_components.strong, {
          children: "AWS external ID"
        }), ", as shown below, which you need to enter into an IAM Role you create in your AWS account. Store these values for future reference."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/aws/cross-account-1-generate-credentials.png",
          alt: "AWS credential generation in the Hightouch UI"
        })
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "Neither the Account ID nor the external ID are secrets, so you don't need to worry about securely storing them."
        })
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["From your AWS console, navigate to ", _jsx(_components.strong, {
            children: "Identity and Access Management (IAM)"
          }), " > ", _jsx(_components.strong, {
            children: "Access Management"
          }), " > ", _jsx(_components.strong, {
            children: "Roles"
          }), " and click ", _jsx(_components.strong, {
            children: "Create Role"
          }), ".\n", _jsx(_components.img, {
            src: "integrations/aws/cross-account-1-create-role.png",
            alt: "Role creation in the AWS console"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Select type of trusted entity"
          }), ", choose ", _jsx(_components.strong, {
            children: "AWS account"
          }), ". Then under ", _jsx(_components.strong, {
            children: "An AWS account"
          }), ", select ", _jsx(_components.strong, {
            children: "Another AWS account"
          }), ".\n", _jsx(_components.img, {
            src: "integrations/aws/cross-account-2-create-role.png",
            alt: "Role creation in the AWS console"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Enter the ", _jsx(_components.strong, {
            children: "Account ID"
          }), " you copied from the Hightouch UI, select ", _jsx(_components.strong, {
            children: "Require external ID"
          }), " and enter the external ID you copied from the Hightouch UI. Click ", _jsx(_components.strong, {
            children: "Next"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Attach permissions to the role, choose a role name, then click ", _jsx(_components.strong, {
            children: "Create role"
          }), ". The exact permission policies you attach depend on which Hightouch features you intend to use. Consult the documentation ", _jsx(_components.a, {
            href: "#overview",
            children: "for those services"
          }), " for further guidance.\n", _jsx(_components.img, {
            src: "integrations/aws/cross-account-3-create-role.png",
            alt: "Role creation in the AWS console"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Copy the ", _jsx(_components.strong, {
            children: "Role ARN"
          }), " from AWS IAM and paste it into the ", _jsx(_components.strong, {
            children: "Role ARN"
          }), " field in Hightouch. Click ", _jsx(_components.strong, {
            children: "Create"
          }), " to complete the process.\n", _jsx(_components.img, {
            src: "integrations/aws/cross-account-4-create-role.png",
            alt: "Role creation in the AWS console"
          })]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information, read ", _jsx(_components.a, {
          href: "https://docs.aws.amazon.com/IAM/latest/UserGuide/tutorial_cross-account-with-roles.html",
          children: "AWS's tutorial"
        }), " on delegating access cross-account using IAM Roles."]
      }), "\n", _jsx(_components.h3, {
        id: "access-key",
        children: "Access key"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "Access Key"
        }), " access type allows you to configure Hightouch to use an IAM user by providing the user's ", _jsx(_components.strong, {
          children: "Access Key ID"
        }), " and ", _jsx(_components.strong, {
          children: "Secret Access Key"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["If you need help generating these keys, consult the ", _jsx(_components.a, {
          href: "https://docs.aws.amazon.com/IAM/latest/UserGuide/id_credentials_access-keys.html",
          children: "IAM article"
        }), " on this topic."]
      }), "\n", _jsxs(_components.p, {
        children: ["Once you have an Access Key ID and Secret Access Key, paste those values into the form and click ", _jsx(_components.strong, {
          children: "Create"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/aws/access-key.png",
          alt: "Role creation in the AWS console"
        })
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", "\n", _jsx(_components.h3, {
        id: "credentialserror-missing-credentials-in-configuration",
        children: "CredentialsError: Missing credentials in configuration"
      }), "\n", "\n", _jsxs(_components.p, {
        children: ["If you receive the following error ", _jsx(_components.code, {
          children: "CredentialsError: Missing credentials in config"
        }), ", it means Hightouch can't assume the role required by your Athena or S3 sync, or ", _jsx(_components.a, {
          href: "/security/storage",
          children: "external storage"
        }), " configuration."]
      }), "\n", _jsxs(_components.p, {
        children: ["This error is often due to an issue with the ", _jsx(_components.a, {
          href: "https://aws.amazon.com/blogs/security/how-to-use-trust-policies-with-iam-roles/",
          children: "IAM trust policy"
        }), ". This policy defines which other AWS accounts can assume roles in your account. When you set up AWS credentials in Hightouch using a ", _jsx(_components.a, {
          href: "#cross-account-role",
          children: "cross-account role"
        }), ", we provide you with Hightouch's Account ID and an additional external ID. You should include Hightouch's Account ID in your role trust policy, and can optionally include the external ID."]
      }), "\n", _jsx(_components.p, {
        children: "To troubleshoot this error, follow these steps:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Ensure that the role exists."
        }), "\n", _jsx(_components.li, {
          children: "Ensure that the role has a trust policy attached."
        }), "\n", _jsxs(_components.li, {
          children: ["Ensure that the role's trust policy includes our Hightouch AWS account ID (", _jsx(_components.code, {
            children: "324528010823"
          }), ") as a principal."]
        }), "\n", _jsx(_components.li, {
          children: "If the role trust policy includes an external ID check, ensure it matches the external ID generated by Hightouch. See the example below:"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/aws/trusted-entity.png",
          alt: "Trust policy in the AWS console"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["If you want to check the external ID but misplaced the external ID that Hightouch generates, you need to ", _jsx(_components.a, {
          href: "#setup",
          children: "re-create the cloud provider integration in Hightouch"
        }), ". Hightouch only displays this value once during cloud provider setup."]
      })]
    });
  }
}
export default MDXContent;
